<template>
  <div class="yt-page">
    <rmr-page-hero title="Dashboard" title-accent="YT" :icon="icons.mdiYoutube">
      <template #subtitle>
        <p class="yt-page__update">
          {{ periodLabel }}
          <span v-if="lastDate" class="yt-page__muted">&mdash; dernière donnée disponible : {{ fDayLong(lastDate) }}</span>
          <span v-if="discoverLastDate" class="yt-page__muted"> · Discover : {{ fDayLong(discoverLastDate) }}</span>
        </p>
      </template>
      <template #actions>
        <rmr-action-button :label="exporting ? 'Extraction…' : 'Extraire en Excel'" :icon="icons.mdiMicrosoftExcel" variant="primary" :loading="exporting" :disabled="exporting || !options" @click="exportExcel" />
      </template>
    </rmr-page-hero>

    <div class="yt-filters">
      <v-card flat class="yt-filters__card">
        <div class="yt-filters__row">
          <div class="yt-filter">
            <label class="yt-filter__label">Période</label>
            <rmr-select :value="period" :items="periodItems" item-text="text" item-value="value" @input="onPeriod" />
          </div>
          <div v-if="period === 'custom'" class="yt-filter yt-filter--range">
            <label class="yt-filter__label">Du → au</label>
            <rmr-date-picker-field
              :key="rangeKey"
              :value="rangeDraft"
              :display-value="`${fDayLong(range.from)} — ${fDayLong(range.to)}`"
              :menu.sync="rangeMenu"
              range
              :picker-props="{ max: lastDate || undefined, min: '2025-01-01' }"
              prepend-inner-icon="mdi-calendar-range"
              @input="onRangePicked"
            />
          </div>
          <div class="yt-filter yt-filter--grow">
            <label class="yt-filter__label">Univers</label>
            <rmr-select :value="siteGroupId" :items="siteGroupItems" item-text="name" item-value="id" placeholder="Tous les univers" clearable @input="onSiteGroup" />
          </div>
          <div class="yt-filter yt-filter--grow">
            <label class="yt-filter__label">Site</label>
            <rmr-select :value="siteId" :items="siteItems" item-text="name" item-value="id" placeholder="Tous les sites" clearable @input="onSite" />
          </div>
          <div class="yt-filter">
            <label class="yt-filter__label">Ancienneté</label>
            <rmr-segmented-control :value="age" :items="ageItems" size="md" @input="onAge" />
          </div>
          <div class="yt-filter yt-filter--action">
            <rmr-action-button label="Réinitialiser" :icon="icons.mdiRestore" variant="utility" @click="resetFilters" />
          </div>
        </div>
        <p v-if="!hasData" class="yt-filters__notice">
          Aucune donnée YouTube collectée pour l'instant : le dashboard se remplira après la première exécution du crawler.
        </p>
      </v-card>
    </div>

    <div class="yt-nav">
      <rmr-tabs v-model="activeTab" :tabs="tabs" @input="onTabChange" />
    </div>

    <v-tabs-items v-model="activeTab" class="yt-content">
      <v-tab-item><yt-overview-tab :active="activeTab === 0" /></v-tab-item>
      <v-tab-item><yt-trends-tab :active="activeTab === 1" /></v-tab-item>
      <v-tab-item><yt-videos-tab :active="activeTab === 2" @open-video="openVideo" /></v-tab-item>
      <v-tab-item><yt-monetization-tab :active="activeTab === 3" /></v-tab-item>
      <v-tab-item><yt-discover-tab :active="activeTab === 4" @open-video="openVideo" /></v-tab-item>
      <v-tab-item><yt-detail-tab :active="activeTab === 5" @open-video="openVideo" /></v-tab-item>
    </v-tabs-items>

    <yt-video-modal v-model="videoModal" :video-id="videoId" />
  </div>
</template>

<script>
import {
  mdiYoutube, mdiMicrosoftExcel, mdiRestore, mdiViewDashboardOutline, mdiChartLine, mdiPlayBoxMultipleOutline,
  mdiCurrencyEur, mdiGoogle, mdiTableLarge,
} from '@mdi/js'
import { computed, nextTick, onMounted, ref } from '@vue/composition-api'
import RmrActionButton from '@/components/ui/actions/RmrActionButton.vue'
import RmrSegmentedControl from '@/components/ui/actions/RmrSegmentedControl.vue'
import RmrDatePickerField from '@/components/ui/forms/RmrDatePickerField.vue'
import RmrSelect from '@/components/ui/forms/RmrSelect.vue'
import RmrPageHero from '@/components/ui/layout/RmrPageHero.vue'
import RmrTabs from '@/components/ui/layout/RmrTabs.vue'
import YtOverviewTab from '@/components/audience/youtube-perf/tabs/YtOverviewTab.vue'
import YtTrendsTab from '@/components/audience/youtube-perf/tabs/YtTrendsTab.vue'
import YtVideosTab from '@/components/audience/youtube-perf/tabs/YtVideosTab.vue'
import YtMonetizationTab from '@/components/audience/youtube-perf/tabs/YtMonetizationTab.vue'
import YtDiscoverTab from '@/components/audience/youtube-perf/tabs/YtDiscoverTab.vue'
import YtDetailTab from '@/components/audience/youtube-perf/tabs/YtDetailTab.vue'
import YtVideoModal from '@/components/audience/youtube-perf/modals/YtVideoModal.vue'
import useYoutubePerf, { AGE_ITEMS, API_BASE, PERIOD_ITEMS } from '@/components/audience/youtube-perf/useYoutubePerf'
import { fDayLong } from '@/components/audience/youtube-perf/ytFormat'
import useProxy from '@/hooks/useProxy'
import { downloadBlobFile, extractBlobErrorMessage, getFilenameFromDisposition } from '@/utils/download'
import { saveRmraCustomEvent, saveRmraView } from '@/utils/rmr-analytics'

const COMPONENT_NAME = 'DashboardYoutube'

const icons = { mdiYoutube, mdiMicrosoftExcel, mdiRestore, mdiViewDashboardOutline, mdiChartLine, mdiPlayBoxMultipleOutline, mdiCurrencyEur, mdiGoogle, mdiTableLarge }

export default {
  name: COMPONENT_NAME,
  components: {
    RmrActionButton, RmrSegmentedControl, RmrDatePickerField, RmrSelect, RmrPageHero, RmrTabs,
    YtOverviewTab, YtTrendsTab, YtVideosTab, YtMonetizationTab, YtDiscoverTab, YtDetailTab, YtVideoModal,
  },
  setup() {
    const { route } = useProxy()
    const { store, axiosGet, params, range, label, period, lastDate } = useYoutubePerf()

    // => CONST
    const activeTab = ref(0)
    const tabs = [
      { key: 'overview', label: "Vue d'ensemble", icon: icons.mdiViewDashboardOutline },
      { key: 'trends', label: 'Tendances', icon: icons.mdiChartLine },
      { key: 'videos', label: 'Vidéos et sources', icon: icons.mdiPlayBoxMultipleOutline },
      { key: 'monetization', label: 'Monétisation', icon: icons.mdiCurrencyEur },
      { key: 'discover', label: 'Focus Discover', icon: icons.mdiGoogle },
      { key: 'detail', label: 'Détail des vidéos', icon: icons.mdiTableLarge },
    ]
    const rangeMenu = ref(false)
    const rangeDraft = ref([])
    const rangeKey = ref(0)
    const exporting = ref(false)
    const videoModal = ref(false)
    const videoId = ref(null)

    // => COMPUTED
    const options = computed(() => store.getters['youtubePerf/getOptions'])
    const siteGroupId = computed(() => store.getters['youtubePerf/getSiteGroupId'])
    const siteId = computed(() => store.getters['youtubePerf/getSiteId'])
    const age = computed(() => store.getters['youtubePerf/getAge'])
    const siteGroupItems = computed(() => options.value?.site_groups || [])
    const siteItems = computed(() => store.getters['youtubePerf/getSites'])
    const discoverLastDate = computed(() => options.value?.discover_last_date || null)
    const hasData = computed(() => !options.value || Boolean(options.value.last_date))

    // => METHODS
    const onPeriod = (value) => {
      if (value === 'custom') {
        rangeDraft.value = [range.value.from, range.value.to]
        rangeKey.value += 1
        store.dispatch('youtubePerf/updateCustomRange', { from: range.value.from, to: range.value.to })
        return
      }
      store.dispatch('youtubePerf/updatePeriod', value)
    }
    const onRangePicked = (values) => {
      if (!Array.isArray(values) || values.length < 2) {
        rangeDraft.value = values || []
        return
      }
      const [from, to] = [...values].filter(Boolean).sort()
      rangeDraft.value = [from, to]
      rangeMenu.value = false
      store.dispatch('youtubePerf/updateCustomRange', { from, to })
    }
    const onSiteGroup = (value) => store.dispatch('youtubePerf/updateSiteGroup', value)
    const onSite = (value) => store.dispatch('youtubePerf/updateSite', value)
    const onAge = (value) => store.dispatch('youtubePerf/updateAge', value)
    const resetFilters = () => store.dispatch('youtubePerf/resetFilters')
    const onTabChange = () => nextTick(() => window.dispatchEvent(new Event('resize')))
    const openVideo = (id) => {
      videoId.value = id
      videoModal.value = true
    }
    const loadOptions = async () => {
      const { data } = await axiosGet(`${API_BASE}/filters`)
      store.commit('youtubePerf/setOptions', data)
      store.commit('youtubePerf/bumpFiltersVersion')
    }
    const exportExcel = async () => {
      exporting.value = true
      try {
        const response = await axiosGet(`${API_BASE}/export_xlsx`, { ...params.value, period_label: label.value }, { responseType: 'blob' })
        const filename = getFilenameFromDisposition(response.headers?.['content-disposition'], `dashboard-youtube_${range.value.from}_${range.value.to}.xlsx`)
        downloadBlobFile(response.data, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        saveRmraCustomEvent(route, 'export')
      } catch (error) {
        const message = await extractBlobErrorMessage(error)
        store.dispatch('app/setSnackBar', { show: true, text: message || "L'extraction a échoué", color: 'error', timeout: 4000 })
      } finally {
        exporting.value = false
      }
    }

    // => HOOKS
    onMounted(async () => {
      saveRmraView(route)
      await loadOptions()
    })

    return {
      icons, tabs, activeTab, options, period, periodItems: PERIOD_ITEMS, ageItems: AGE_ITEMS, range, rangeMenu, rangeDraft, rangeKey,
      periodLabel: label, lastDate, discoverLastDate, hasData, siteGroupId, siteId, age, siteGroupItems, siteItems, exporting,
      videoModal, videoId, onPeriod, onRangePicked, onSiteGroup, onSite, onAge, resetFilters, onTabChange, openVideo, exportExcel, fDayLong,
    }
  },
}
</script>

<style lang="scss" scoped>
.yt-page {
  background: #F1F5F9;
  min-height: 100vh;
  padding-bottom: 48px;
}

.yt-page__update {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin: 4px 0 0;
  font-weight: 500;
}

.yt-page__muted { color: rgba(255, 255, 255, 0.4); }

.yt-filters {
  max-width: 1440px;
  margin: -16px auto 0;
  padding: 0 24px;
  position: relative;
  z-index: 5;
}

.yt-filters__card {
  border-radius: 14px !important;
  padding: 16px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.yt-filters__row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.yt-filters__notice {
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: $rmr-radius-md;
  background: $rmr-indigo-bg;
  color: $rmr-indigo-dark;
  font-size: $rmr-fs-sm;
  font-weight: 600;
}

.yt-filter { min-width: 180px; }
.yt-filter--range { flex: 0 0 300px; }
.yt-filter--grow { flex: 1; min-width: 200px; }
.yt-filter--action { min-width: auto; flex: 0; }

.yt-filter__label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: $rmr-text-subtle;
  margin-bottom: 6px;
}

.yt-nav {
  max-width: 1440px;
  margin: 24px auto 0;
  padding: 0 24px;
}

.yt-content {
  max-width: 1440px;
  margin: 24px auto 0;
  padding: 0 24px;
  background: transparent !important;
}
</style>
